import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ProductListQuery } from '@/api/handlers/resources/products/requests';
import type { GetProductListResponse } from '@/api/handlers/resources/products/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from './productQueryKeys';

/**
 * Paginated, filtered query for the Products management table. Backed by a
 * plain `useQuery` (not infinite) because the table renders a classic
 * page-at-a-time pager, not an infinite scroll.
 *
 * `placeholderData: keepPreviousData` keeps the current page on screen while
 * the next page / filter result loads, so paging doesn't flash an empty grid.
 * The `queryKey` carries the whole `query` object, so changing any filter or
 * the page resets to a fresh cache entry automatically.
 */
export const useProductListQuery = (
    query: ProductListQuery,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetProductListResponse>>({
        queryKey: ProductQueryKeys.list(query),
        queryFn: () => apiClient.products.getProductList(query),
        enabled,
        placeholderData: keepPreviousData,
        staleTime: 60_000,
    });
};
